import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    DESIGN_LIVE_REVIEW_METHOD,
    bugtracker,
    hasCollaborationMethodOption,
    mergeServiceProductOptions,
} from '@99designs/design-services-common';
import {
    ProductOptionsDocument,
    ProductOptionsQuery,
    ProductOptionsQueryVariables,
} from '../../../../graph/ProductOptions.generated';
import { useBriefContext } from '../../../BriefContext';
import { DesignServiceRequestType } from '../../../Submit/consts';
import { useResolveServiceProductMpvId } from '../../hooks/useResolveServiceProductMpvId';
import { useSelectProductWithCacheUpdate } from '../ClientConfiguredFields/ProductDropdown/useSelectProductMutation';
import { useUpdateField } from '../useUpdateField';
import { useBriefSetGridChoiceMutation } from './briefSetGridChoice.generated';
import {
    ServiceOptionsUpdatedDocument,
    ServiceOptionsUpdatedMutation,
    ServiceOptionsUpdatedMutationVariables,
} from './serviceOptionsUpdated.generated';

export function useUpdateGridChoiceField(
    key: string,
    briefId?: string,
    initialValue?: string | null
) {
    const client = useApolloClient();
    const [selectProduct] = useSelectProductWithCacheUpdate();
    const { expertService, serviceProductOptions } = useBriefContext();
    const printProductMpvId = expertService?.printProduct?.mpvId;
    const resolveServiceProductMpvId = useResolveServiceProductMpvId();

    const [setGridChoice] = useBriefSetGridChoiceMutation();

    const updateField = useUpdateField<string>({
        key,
        setField: setGridChoice,
        briefId,
        initialValue,
    });

    return useCallback(
        async (value: string) => {
            const result = await updateField(value);
            const briefData = result?.data;
            if (!briefData?.setField) {
                return;
            }

            if (key !== 'requestType') {
                return;
            }

            const field = briefData.setField.dynamicFields.find((f) => f.id === key);
            if (!field) {
                return;
            }
            if (field.__typename !== 'GridChoiceField') {
                throw new Error(
                    "Field is not a GridChoiceField. This schema is not compatible with the fields currently in use on the front end. And will cause all kinds of trouble. If you're seeing this error in bugsnag, investigate immediately"
                );
            }
            const choiceValue = field.choiceValue as DesignServiceRequestType | null;
            if (!field?.choiceValue) {
                return;
            }

            const correlationId = briefData.setField.correlationId;
            if (!correlationId || !printProductMpvId) {
                return;
            }

            // Check if the current product supports CollaborationMethodOption
            const productKey = expertService?.serviceProduct?.productKey;
            const version = expertService?.serviceProduct?.pimMerchantVersion;

            if (!productKey || !version) {
                return;
            }

            try {
                const { data: productOptionsData } = await client.query<
                    ProductOptionsQuery,
                    ProductOptionsQueryVariables
                >({
                    query: ProductOptionsDocument,
                    variables: { key: productKey, version: version },
                });

                const productOptions = productOptionsData?.productOptions ?? [];

                if (hasCollaborationMethodOption(productOptions)) {
                    const isDesignLive = choiceValue === DESIGN_LIVE_REVIEW_METHOD;
                    const merged = mergeServiceProductOptions(
                        productOptions,
                        serviceProductOptions,
                        isDesignLive
                    );

                    if (merged.length > 0) {
                        await client.mutate<
                            ServiceOptionsUpdatedMutation,
                            ServiceOptionsUpdatedMutationVariables
                        >({
                            mutation: ServiceOptionsUpdatedDocument,
                            variables: {
                                requestId: correlationId,
                                input: { options: merged },
                            },
                        });
                    }
                    return;
                }
            } catch (err) {
                bugtracker.notify(
                    err instanceof Error
                        ? err
                        : new Error(
                              `supportsCollaborationMethod check failed in grid choice: ${String(
                                  err
                              )}`
                          )
                );
                return;
            }

            // Product does NOT support CollaborationMethodOption — fall back to selectProduct
            const resolvedMpvId =
                choiceValue === DESIGN_LIVE_REVIEW_METHOD
                    ? DESIGN_LIVE_REVIEW_METHOD
                    : await resolveServiceProductMpvId({
                          printProductMpvId,
                      });

            await selectProduct({
                variables: {
                    requestId: correlationId,
                    input: {
                        serviceProductMpvId: resolvedMpvId,
                        printProductMpvId: [],
                    },
                },
            });
        },
        [
            updateField,
            key,
            printProductMpvId,
            expertService,
            serviceProductOptions,
            resolveServiceProductMpvId,
            selectProduct,
            client,
        ]
    );
}
